import { useContext, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import Container from '@/components/Container/Container';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import { GlobalContext } from '@/pages/_app';

const observerOptions = {
	root: null,
	rootMargin: '0px',
	threshold: 0.35,
};

const observerAction = (
	entry,
	changeUniqueUsers,
	changeIteractions,
	changeSpentTime,
	changeMessages,
) => {
	if (entry.isIntersecting) {
		const uniqueUsersInterval = setInterval(() => {
			changeUniqueUsers((data) => {
				if (data >= 10) {
					clearInterval(uniqueUsersInterval);
					return data;
				}

				return data + 1;
			});
		}, 110);

		setTimeout(() => {
			const iteractionsInterval = setInterval(() => {
				changeIteractions((data) => {
					if (data >= 70) {
						clearInterval(iteractionsInterval);
						return data;
					}

					return data + 1;
				});
			}, 38);
		}, 400);

		setTimeout(() => {
			const spentTimerIteractions = setInterval(() => {
				changeSpentTime((data) => {
					if (data >= 35) {
						clearInterval(spentTimerIteractions);
						return data;
					}

					return data + 1;
				});
			}, 63);
		}, 550);

		setTimeout(() => {
			const messagesTimer = setInterval(() => {
				changeMessages((data) => {
					if (data >= 30) {
						clearInterval(messagesTimer);
						return data;
					}

					return data + 1;
				});
			}, 80);
		}, 800);

		entry.target.classList.add('active');
	}
};

const Statistics = ({ data }) => {
	const { lang } = useContext(GlobalContext);
	const rowNode = useRef<any>(null);
	const [countries, changeCountries] = useState(0);
	const [iteractions, changeIteractions] = useState(48);
	const [spentTime, changeSpentTime] = useState(20);
	const [messages, changeMessages] = useState(14);

	useEffect(() => {
		if (!rowNode.current) return;

		const observer = new IntersectionObserver(
			([entry]) =>
				observerAction(
					entry,
					changeCountries,
					changeIteractions,
					changeSpentTime,
					changeMessages,
				),
			observerOptions,
		);

		observer.observe(rowNode.current);

		return () => observer.disconnect();
	}, [rowNode]);

	return (
		<Section>
			<Container>
				<Row ref={rowNode}>
					<Left>
						<Title>
							{translate(lang, {
								en: 'Growing globally',
								ru: 'Глобальная аудитория',
								ua: 'Глобальний ріст',
							})}
						</Title>

						<InnerRow>
							<Col className="left-side-column">
								<NumberRow>
									<NumberValue>{countries * 19}</NumberValue>

									<div>
										<Symbol>+</Symbol>

										<Subtitle>{parseHTML(data?.[0]?.unit)}</Subtitle>
									</div>
								</NumberRow>

								<Description>{parseHTML(data?.[0]?.description)}</Description>
							</Col>
						</InnerRow>
					</Left>

					<Right>
						<Title>
							{translate(lang, {
								en: 'Actively engaged',
								ru: 'Активные и вовлеченные пользователи',
								ua: 'Активна аудиторія',
							})}
						</Title>

						<InnerRow>
							<Col className="right-side-column">
								<NumberRow>
									<NumberValue>{iteractions}</NumberValue>

									<div>
										<Symbol>+</Symbol>

										<Subtitle>{parseHTML(data?.[1]?.unit)}</Subtitle>
									</div>
								</NumberRow>

								<Description>{parseHTML(data?.[1]?.description)}</Description>
							</Col>

							<Col className="right-side-column">
								<NumberRow>
									<NumberValue>{spentTime}</NumberValue>

									<div>
										<Symbol>+</Symbol>

										<Subtitle>{parseHTML(data?.[2]?.unit)}</Subtitle>
									</div>
								</NumberRow>

								<Description>{parseHTML(data?.[2]?.description)}</Description>
							</Col>

							<Col className="right-side-column">
								<NumberRow>
									<NumberValue>{messages}</NumberValue>

									<div>
										<Symbol>+</Symbol>

										<Subtitle>{parseHTML(data?.[3]?.unit)}</Subtitle>
									</div>
								</NumberRow>

								<Description>{parseHTML(data?.[3]?.description)}</Description>
							</Col>
						</InnerRow>
					</Right>
				</Row>
			</Container>
		</Section>
	);
};

const Section = styled.section`
	max-width: 1920px;
	margin: 0 auto;
	border-top: 2px solid var(--color-white-30);
	border-bottom: 2px solid var(--color-white-30);
	position: relative;
`;

const Left = styled.div`
	width: 31.3%;
	padding-top: 53px;
	padding-bottom: 56px;

	@media (max-width: 1050px) {
		padding-top: 43px;
		padding-bottom: 43px;
	}

	@media (max-width: 600px) {
		width: 100%;
		padding: 0;
		padding-top: 17px;
		padding-bottom: 11px;
		border-left: none;
	}
`;

const Right = styled.div`
	width: 68.6%;
	border-left: 2px solid var(--color-white-30);
	padding-left: 58px;
	padding-top: 53px;
	padding-bottom: 56px;

	p {
		max-width: 190px;
	}

	@media (max-width: 1150px) {
		padding-left: 48px;
	}

	@media (max-width: 1050px) {
		padding-left: 46px;
		padding-top: 43px;
		padding-bottom: 43px;
	}

	@media (max-width: 600px) {
		width: 100%;
		padding: 0;
		padding-top: 17px;
		padding-bottom: 11px;
		border-left: none;
	}
`;

const InnerRow = styled.div`
	display: flex;

	@media (max-width: 800px) {
		flex-wrap: wrap;
	}

	@media (max-width: 600px) {
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}
`;

const Col = styled.div`
	flex-grow: 1;

	@media (max-width: 800px) {
		margin-bottom: 20px;
	}

	@media (max-width: 600px) {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		max-width: 280px;
		padding-right: 0;
		margin-top: 11px;
		margin-bottom: 11px;
	}
`;

const Title = styled.h3`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 25px;
	color: var(--color-black);
	margin-bottom: 23px;

	@media (max-width: 1150px) {
		font-size: 25px;
	}

	@media (max-width: 600px) {
		font-size: 22px;
		line-height: 25px;
		width: 350px;
		max-width: 100%;
		margin: 0 auto;
		margin-bottom: 11px;
	}
`;

const NumberRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	margin-bottom: 20px;

	@media (max-width: 1030px) {
		margin-bottom: 9px;
	}

	@media (max-width: 600px) {
		margin-bottom: 0;
	}
`;

const NumberValue = styled.div`
	font-family: var(--font-sans);

	font-size: 100px;
	line-height: 80px;
	color: var(--color-purple);
	margin-right: 3px;

	@media (max-width: 1300px) {
		font-size: 90px;
	}

	@media (max-width: 1150px) {
		font-size: 80px;
	}

	@media (max-width: 1030px) {
		font-size: 65px;
	}

	@media (max-width: 600px) {
		font-size: 40px;
		line-height: 50px;
	}
`;

const Symbol = styled.div`
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	color: var(--color-purple);

	@media (max-width: 1150px) {
		font-size: 38px;
		line-height: 45px;
	}

	@media (max-width: 1030px) {
		font-size: 30px;
		line-height: 33px;
	}

	@media (max-width: 600px) {
		font-size: 30px;
		line-height: 25px;
		transform: translateY(3px);
	}
`;

const Subtitle = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 15px;
	color: var(--color-purple);

	@media (max-width: 1150px) {
		font-size: 17px;
	}

	@media (max-width: 1030px) {
		font-size: 15px;
		line-height: 15px;
	}

	@media (max-width: 600px) {
		font-size: 16px;
		line-height: 20px;
		transform: translateY(-7px);
	}
`;

const Description = styled.div`
	font-family: var(--font-sans);

	font-size: 18px;
	line-height: 25px;
	color: var(--color-purple-50);

	@media (max-width: 1300px) {
		font-size: 17px;
	}

	@media (max-width: 1150px) {
		font-size: 16px;
	}

	@media (max-width: 1030px) {
		font-size: 15px;
		line-height: 21px;
	}

	@media (max-width: 600px) {
		font-size: 16px;
		line-height: 25px;
		width: 126px;
		font-size: 16px;
		line-height: 20px;
	}
`;

const descriptionAnimation = keyframes`
  0% {
    transform: translateY(18px);
    opacity: 0;
  }

  100% {
    transform: translateY(0px);
    opacity: 1;
  }
`;

const numberAnimation = keyframes`
  0% {
    transform: translateY(5px);
    opacity: 0;
  }

  100% {
    transform: translateY(0px);
    opacity: 1;
  }
`;

const symbolAnimation = keyframes`
  0% {
    transform: translateX(15px);
    opacity: 0;
  }

  100% {
    transform: translateX(0px);
    opacity: 1;
  }
`;

const subtitleAnimation = keyframes`
  0% {
    transform: translateX(20px);
    opacity: 0;
  }

  100% {
    transform: translateX(0px);
    opacity: 1;
  }
`;

const fadeInTitle = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const Row = styled.div`
	display: flex;
	position: relative;
	z-index: 1000;
	background-color: var(--color-white);

	${Description} {
		transform: translateY(18px);
		opacity: 0;
	}

	${NumberValue} {
		transform: translateY(5px);
		opacity: 0;
	}

	${Symbol} {
		transform: translateX(15px);
		opacity: 0;
	}

	${Subtitle} {
		transform: translateX(20px);
		opacity: 0;
	}

	${Title} {
		opacity: 0;
	}

	&.active {
		${Left} {
			${Title} {
				animation: ${fadeInTitle} 0.8s 1 forwards;
			}
		}

		${Right} {
			${Title} {
				animation: ${fadeInTitle} 0.8s 0.4s 1 forwards;
			}
		}

		.left-side-column {
			${Description} {
				animation: ${descriptionAnimation} 0.5s 0.3s 1 forwards;
			}
			${NumberValue} {
				animation: ${numberAnimation} 0.5s 1 forwards;
			}
			${Symbol} {
				animation: ${symbolAnimation} 0.3s 0.3s 1 forwards;
			}
			${Subtitle} {
				animation: ${subtitleAnimation} 0.3s 0.3s 1 forwards;
			}
		}

		.right-side-column {
			&:nth-of-type(1) {
				${Description} {
					animation: ${descriptionAnimation} 0.5s 0.6s 1 forwards;
				}
				${NumberValue} {
					animation: ${numberAnimation} 0.5s 0.3s 1 forwards;
				}
				${Symbol} {
					animation: ${symbolAnimation} 0.3s 0.3s 1 forwards;
				}
				${Subtitle} {
					animation: ${subtitleAnimation} 0.3s 0.3s 1 forwards;
				}
			}

			&:nth-of-type(2) {
				${Description} {
					animation: ${descriptionAnimation} 0.5s 0.8s 1 forwards;
				}
				${NumberValue} {
					animation: ${numberAnimation} 0.5s 0.5s 1 forwards;
				}
				${Symbol} {
					animation: ${symbolAnimation} 0.3s 0.5s 1 forwards;
				}
				${Subtitle} {
					animation: ${subtitleAnimation} 0.3s 0.5s 1 forwards;
				}
			}

			&:nth-of-type(3) {
				${Description} {
					animation: ${descriptionAnimation} 0.5s 1s 1 forwards;
				}
				${NumberValue} {
					animation: ${numberAnimation} 0.5s 0.7s 1 forwards;
				}
				${Symbol} {
					animation: ${symbolAnimation} 0.3s 0.7s 1 forwards;
				}
				${Subtitle} {
					animation: ${subtitleAnimation} 0.3s 0.7s 1 forwards;
				}
			}
		}
	}

	@media (max-width: 600px) {
		flex-wrap: wrap;
		flex-direction: column;
	}
`;

export default Statistics;
